import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { hydrateAccountUiPrefsFromCloud } from '../utils/accountUiPrefs';

export interface Profile {
  id: string;
  displayName: string;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

async function applyCloudDesignerPrefs(userId: string) {
  const prefs = await hydrateAccountUiPrefsFromCloud(userId);
  if (!prefs) return;
  const { useProjectStore } = await import('./projectStore');
  useProjectStore.getState().applyAccountUiPrefs(prefs);
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return { id: data.id, displayName: data.display_name };
}

/** Display name for stock log attribution */
export function getActedByName(): string | null {
  const { profile, user } = useAuthStore.getState();
  return profile?.displayName ?? user?.email?.split('@')[0] ?? null;
}

let authListenerAttached = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  profile: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  initialize: async () => {
    if (get().isInitialized) return;

    const { data: { session } } = await supabase.auth.getSession();
    let profile: Profile | null = null;
    if (session?.user) {
      profile = await fetchProfile(session.user.id);
      await applyCloudDesignerPrefs(session.user.id);
    }
    set({ session, user: session?.user ?? null, profile, isInitialized: true });

    if (!authListenerAttached) {
      authListenerAttached = true;
      // IMPORTANT: this callback runs while supabase-js holds the auth lock
      // (navigator.locks). Do NOT await any Supabase call here — calling
      // supabase.from(...)/auth inside would try to re-acquire the same lock
      // and deadlock it, freezing every subsequent query until a page refresh.
      // So we only update session state synchronously and defer the profile
      // fetch to a later tick (outside the lock) via setTimeout.
      supabase.auth.onAuthStateChange((event, nextSession) => {
        set({ session: nextSession, user: nextSession?.user ?? null });

        if (nextSession?.user) {
          const userId = nextSession.user.id;
          // TOKEN_REFRESHED (and similar) fires when returning to a backgrounded tab.
          // Re-applying cloud designer prefs there overwrites live UI (e.g. briefly
          // shows the floating port preview) and restarts realtime — skip it.
          const shouldHydrateDesignerPrefs =
            event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'PASSWORD_RECOVERY';
          setTimeout(() => {
            fetchProfile(userId)
              .then(async (profile) => {
                // Guard against a race where the user changed/signed out meanwhile.
                if (useAuthStore.getState().user?.id === userId) {
                  set({ profile });
                  if (shouldHydrateDesignerPrefs) {
                    await applyCloudDesignerPrefs(userId);
                  }
                }
              })
              .catch(() => {});
          }, 0);
        } else {
          set({ profile: null });
        }
      });
    }
  },

  signIn: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }
    const profile = data.user ? await fetchProfile(data.user.id) : null;
    if (data.user) {
      await applyCloudDesignerPrefs(data.user.id);
    }
    set({
      session: data.session,
      user: data.user,
      profile,
      isLoading: false,
      error: null,
    });
  },

  signOut: async () => {
    set({ isLoading: true, error: null });
    await supabase.auth.signOut();
    set({ session: null, user: null, profile: null, isLoading: false });
  },

  clearError: () => set({ error: null }),
}));
